import { Bug, ImageIcon, Lightbulb, Loader2, MessageSquarePlus, Send, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/components/shared/Toast";
import { getApiBaseUrl } from "@/config/runtime";
import { getHeaders } from "@/lib/api";

type FeedbackCategory = "bug" | "feature";

export function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [screenshotPreviews, setScreenshotPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showToast } = useToast();

  // Clear any pending post-close reset timer on unmount so it can't fire after
  // the component is gone (which would setState on an unmounted component — and
  // throws "window is not defined" when jsdom is torn down mid-timer in tests).
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const resetForm = useCallback(() => {
    setCategory("bug");
    setTitle("");
    setDescription("");
    setScreenshots([]);
    setScreenshotPreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Don't reset immediately - let the animation finish
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(resetForm, 200);
  }, [resetForm]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const imageFiles = files.filter((f) => f.type.startsWith("image/"));

      if (imageFiles.length === 0) return;

      // Limit total screenshots to 5
      const remaining = 5 - screenshots.length;
      const toAdd = imageFiles.slice(0, remaining);

      setScreenshots((prev) => [...prev, ...toAdd]);
      setScreenshotPreviews((prev) => [...prev, ...toAdd.map((f) => URL.createObjectURL(f))]);

      // Reset the input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [screenshots.length],
  );

  const removeScreenshot = useCallback((index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
    setScreenshotPreviews((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const imageItems = items.filter((item) => item.type.startsWith("image/"));

      if (imageItems.length === 0) return;

      e.preventDefault();

      const remaining = 5 - screenshots.length;
      const toProcess = imageItems.slice(0, remaining);

      toProcess.forEach((item) => {
        const file = item.getAsFile();
        if (file) {
          setScreenshots((prev) => [...prev, file]);
          setScreenshotPreviews((prev) => [...prev, URL.createObjectURL(file)]);
        }
      });
    },
    [screenshots.length],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!title.trim() || !description.trim()) {
        showToast("Please fill in the title and description", "warning");
        return;
      }

      setIsSubmitting(true);

      try {
        const headers = await getHeaders();
        // Remove Content-Type so browser sets multipart boundary
        headers.delete("Content-Type");

        const formData = new FormData();
        formData.append("category", category);
        formData.append("title", title.trim());
        formData.append("description", description.trim());
        formData.append("page_url", globalThis.location.href);
        formData.append("user_agent", navigator.userAgent);

        screenshots.forEach((file) => {
          formData.append("screenshots", file);
        });

        const response = await fetch(`${getApiBaseUrl()}/feedback`, {
          method: "POST",
          headers,
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to submit feedback: ${response.statusText}`);
        }

        showToast(
          category === "bug" ? "Bug report submitted. Thank you!" : "Feature request submitted. Thank you!",
          "success",
        );
        handleClose();
      } catch (err) {
        console.error("Failed to submit feedback:", err);
        showToast("Failed to submit feedback. Please try again.", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [category, title, description, screenshots, showToast, handleClose],
  );

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-14 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-brand-gradient text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 group"
        title="Submit feedback"
      >
        <MessageSquarePlus className="w-5 h-5" />
        <span className="text-sm font-medium">Feedback</span>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-surface-overlay backdrop-blur-xs"
            role="presentation" // NOSONAR — decorative backdrop element
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleClose();
            }}
          />

          {/* Modal */}
          <div className="relative w-full max-w-lg bg-surface-primary border border-border-secondary rounded-xl shadow-2xl animate-in zoom-in-95 fade-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary">
              <h2 className="text-lg font-semibold text-content-primary">Send Feedback</h2>
              <button
                onClick={handleClose}
                className="p-1.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-active rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} onPaste={handlePaste}>
              <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Category Toggle */}
                <div>
                  <span className="block text-sm font-medium text-content-secondary mb-2">Type</span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCategory("bug")}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                        category === "bug"
                          ? "bg-red-500/15 border-red-500/40 text-red-400"
                          : "bg-surface-secondary border-border-hover text-content-tertiary hover:border-border-hover"
                      }`}
                    >
                      <Bug className="w-4 h-4" />
                      Bug Report
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategory("feature")}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                        category === "feature"
                          ? "bg-purple-500/15 border-purple-500/40 text-purple-400"
                          : "bg-surface-secondary border-border-hover text-content-tertiary hover:border-border-hover"
                      }`}
                    >
                      <Lightbulb className="w-4 h-4" />
                      Feature Request
                    </button>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="feedback-title" className="block text-sm font-medium text-content-secondary mb-1.5">
                    Title
                  </label>
                  <input
                    id="feedback-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={category === "bug" ? "Brief description of the issue" : "What feature would you like?"}
                    className="w-full px-3 py-2 bg-surface-secondary border border-border-hover rounded-lg text-content-primary text-sm placeholder:text-content-quaternary focus:outline-hidden focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-primary/25 transition-colors"
                    maxLength={255}
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label
                    htmlFor="feedback-description"
                    className="block text-sm font-medium text-content-secondary mb-1.5"
                  >
                    {category === "bug" ? "Steps to Reproduce / Details" : "Description"}
                  </label>
                  <textarea
                    id="feedback-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={
                      category === "bug"
                        ? "1. Go to...\n2. Click on...\n3. Expected vs actual behavior..."
                        : "Describe the feature you'd like and how it would help you..."
                    }
                    rows={5}
                    className="w-full px-3 py-2 bg-surface-secondary border border-border-hover rounded-lg text-content-primary text-sm placeholder:text-content-quaternary focus:outline-hidden focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-primary/25 transition-colors resize-none"
                    required
                  />
                </div>

                {/* Screenshot Attachments */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-content-secondary">
                      Screenshots <span className="text-content-quaternary font-normal">(optional)</span>
                    </label>
                    <span className="text-xs text-content-quaternary">{screenshots.length}/5</span>
                  </div>

                  {/* Preview Grid */}
                  {screenshotPreviews.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {screenshotPreviews.map((preview, index) => (
                        <div
                          key={`screenshot-${index}`} // NOSONAR — index key safe for screenshot preview list; items are only appended or removed
                          className="relative group aspect-video bg-surface-secondary rounded-lg overflow-hidden border border-border-hover"
                        >
                          <img src={preview} alt={`Screenshot ${index + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeScreenshot(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500/80 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload Button */}
                  {screenshots.length < 5 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-3 py-2 bg-surface-secondary border border-dashed border-border-hover rounded-lg text-sm text-content-tertiary hover:text-content-secondary hover:border-border-hover transition-colors w-full justify-center"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Attach screenshot or paste from clipboard
                    </button>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-secondary">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-content-tertiary hover:text-content-primary transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !title.trim() || !description.trim()}
                  className="flex items-center gap-2 px-5 py-2 bg-brand-gradient text-white text-sm font-medium rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
